import { useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, UploadCloud, FileText, Image as ImageIcon, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Trade Organization Verification Gate.
 * Locks the dashboard behind a KYC-style upload until the org has
 * provided: Registration Number + Certificate of Registration + Logo.
 */
export default function VerificationGate({ org, onVerified }) {
  const [regNo, setRegNo]       = useState(org?.registration_number || "");
  const [cert, setCert]         = useState(null);
  const [bylaws, setBylaws]     = useState(null);
  const [logo, setLogo]         = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting]   = useState(false);

  const canSubmit = regNo.trim().length >= 3 && cert && bylaws && logo && !submitting;

  const handleLogo = (file) => {
    setLogo(file);
    if (file) setLogoPreview(URL.createObjectURL(file));
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("registration_number", regNo.trim());
      fd.append("certificate", cert);
      fd.append("bylaws", bylaws);
      fd.append("logo", logo);
      const { data } = await api.post(`/orgs/${org.id}/verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Verification submitted — dashboard unlocked");
      onVerified?.(data.organization);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12" data-testid="trade-org-verification-gate">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
        <Lock size={11} /> Trade Organization · Pending KYC
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
        Verify Your Trade Body
      </h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        Before issuing co-branded iCards or addressing members, every Trade
        Organization must upload its <span className="text-white">Certificate of Registration</span>, official
        <span className="text-white"> Registration Number</span>, and <span className="text-white">Logo</span>.
        This becomes the trust seal on every member&rsquo;s digital identity.
      </p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        {/* Form */}
        <div className="cs-card rounded-2xl p-6 sm:p-8 space-y-7" data-testid="verification-form">
          {/* Step 1 — Reg No */}
          <Field
            n={1}
            title="Registration Number"
            hint="Your formal society / trust registration number as printed on the certificate."
          >
            <Input
              value={regNo}
              onChange={(e) => setRegNo(e.target.value)}
              placeholder="e.g. MUM/SOC/12345/2018"
              className="bg-black/30 border-white/15 text-white font-mono tracking-wider"
              data-testid="verify-reg-no-input"
            />
          </Field>

          {/* Step 2 — Certificate */}
          <Field
            n={2}
            title="Certificate of Registration"
            hint="PDF, PNG, or JPG up to 8 MB. Stays private to your committee + Call Sheet admins."
          >
            <FileSlot
              file={cert}
              onFile={setCert}
              accept=".pdf,image/png,image/jpeg,image/webp"
              icon={<FileText size={18} />}
              label="Drop or click to upload certificate"
              testid="verify-certificate-input"
            />
          </Field>

          {/* Step 3 — Society By-Laws */}
          <Field
            n={3}
            title="Society By-Laws"
            hint="Rules & Regulations of the society (mandatory under the Societies Registration Act). PDF/PNG/JPG up to 8 MB."
          >
            <FileSlot
              file={bylaws}
              onFile={setBylaws}
              accept=".pdf,image/png,image/jpeg,image/webp"
              icon={<FileText size={18} />}
              label="Drop or click to upload society by-laws"
              testid="verify-bylaws-input"
            />
          </Field>

          {/* Step 4 — Logo */}
          <Field
            n={4}
            title="Official Logo"
            hint="High-contrast PNG/SVG works best. This logo will appear on every iCard you issue."
          >
            <FileSlot
              file={logo}
              preview={logoPreview}
              onFile={handleLogo}
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              icon={<ImageIcon size={18} />}
              label="Drop or click to upload logo"
              testid="verify-logo-input"
            />
          </Field>

          <div className="pt-2 flex items-center justify-between border-t border-white/10">
            <div className="text-[10px] tracking-[0.25em] uppercase text-white/45 flex items-center gap-1.5">
              <ShieldCheck size={11} className="text-[#F5B82E]" />
              Encrypted at rest · Audit-logged
            </div>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="h-10 px-6 rounded-full text-[11px] uppercase tracking-[0.25em] btn-themed"
              data-testid="submit-verification-btn"
            >
              {submitting
                ? "Uploading…"
                : <><UploadCloud size={14} className="mr-2" /> Submit for verification</>}
            </Button>
          </div>
        </div>

        {/* What unlocks */}
        <aside className="cs-card rounded-2xl p-6 space-y-4 h-fit" data-testid="verification-benefits">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Once Verified</div>
          {[
            "Issue co-branded Digital iCards to your members",
            "Track On-the-Job activity across all sets nationally",
            "Run committee proposals & 3-of-N signature approvals",
            "Open vendor catalog for partner access",
            "Surface your members in studio discovery",
          ].map((b) => (
            <div key={b} className="flex items-start gap-2 text-sm text-white/75">
              <CheckCircle2 size={15} className="mt-0.5 text-emerald-400 shrink-0" />
              {b}
            </div>
          ))}
          <div className="mt-4 pt-4 border-t border-white/10 text-[10px] tracking-[0.22em] uppercase text-white/40">
            Submission auto-approves for v1 · Real review queue coming
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ n, title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <span className="text-[10px] tracking-[0.35em] uppercase text-[#F5B82E] font-mono">{String(n).padStart(2, "0")}</span>
        <Label className="font-display text-base uppercase tracking-tight">{title}</Label>
      </div>
      <p className="mt-1 mb-3 text-xs text-white/50">{hint}</p>
      {children}
    </div>
  );
}

function FileSlot({ file, preview, onFile, accept, icon, label, testid }) {
  return (
    <label
      className="block cursor-pointer group"
      data-testid={`${testid}-label`}
    >
      <input
        type="file"
        accept={accept}
        className="hidden"
        data-testid={testid}
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
      <div className={`rounded-xl border border-dashed transition-all px-4 py-5 flex items-center gap-4
        ${file ? "border-emerald-400/50 bg-emerald-400/5" : "border-white/15 bg-white/[0.02] group-hover:border-white/30 group-hover:bg-white/[0.04]"}`}>
        {preview ? (
          <img src={preview} alt="logo preview" className="w-12 h-12 rounded-lg object-cover bg-white/10" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/55">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {file ? (
            <>
              <div className="text-sm text-white truncate">{file.name}</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300 mt-0.5">
                Ready · {(file.size / 1024).toFixed(0)} KB
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-white/75">{label}</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/40 mt-0.5">
                Click to browse
              </div>
            </>
          )}
        </div>
        <UploadCloud size={16} className="text-white/40 shrink-0" />
      </div>
    </label>
  );
}

// Helper exposed so consumers can build absolute file URLs.
export function fileUrl(path) {
  if (!path) return "";
  return `${API}/files/${path}`;
}
